import { useState, useEffect, Fragment } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SEO from '@/components/SEO';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Phone, MapPin, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultSteps = [
  { num: '1', title: 'Contact Us', desc: 'Fill out the form below or call us with details of what you\'d like to sell.' },
  { num: '2', title: 'Get a Quote', desc: 'We\'ll provide an indicative buy-back price based on current market rates.' },
  { num: '3', title: 'Visit or Send', desc: 'Bring your items to our Bishopdale store or arrange secure shipping.' },
  { num: '4', title: 'Get Paid', desc: 'Once verified, receive payment via bank transfer, typically same day.' },
];

const defaultWhatWeBuy = [
  'Gold bars & coins (all weights)',
  'Silver bars & coins (all weights)',
  'Platinum & palladium items',
  'Gold & silver jewellery / scrap metal',
  'Estate and inherited precious metals',
];

export default function SellPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', message: '', product_name: '' });
  const [metalType, setMetalType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [steps, setSteps] = useState(defaultSteps);
  const [whatWeBuy, setWhatWeBuy] = useState(defaultWhatWeBuy);

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => {
      const c = r.data;
      if (c.sell_step1_title) setSteps(prev => prev.map((s, i) => ({
        ...s,
        title: c[`sell_step${i+1}_title`] || s.title,
        desc: c[`sell_step${i+1}_desc`] || s.desc,
      })));
      if (c.sell_what_we_buy) {
        try {
          const items = JSON.parse(c.sell_what_we_buy);
          if (Array.isArray(items) && items.length) setWhatWeBuy(items);
        } catch {
          const items = c.sell_what_we_buy.split('\n').filter(Boolean);
          if (items.length) setWhatWeBuy(items);
        }
      }
    }).catch(err => {
      console.error('Failed to load Sell page content:', err);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!metalType) { toast.error('Please select a metal type'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, product_name: metalType, quote_type: 'sell' });
      setSubmitted(true);
      toast.success('Your sell quote request has been submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="sell-page">
      <SEO
        title="Sell Gold & Silver"
        description="Sell your gold, silver and platinum bullion to Southern Bullion Exchange in Christchurch. Competitive buy-back prices in NZD. Get a free quote today."
        path="/sell"
      />
      <section className="bg-[#0A0A0A] py-16 text-center">
        <div className="max-w-7xl mx-auto px-6">
          <p className="section-label mb-3">Sell Your Precious Metals</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "Cinzel, serif" }}>Sell to Us</h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            We buy gold, silver, platinum, and palladium bars, coins, and scrap at competitive NZD prices.
          </p>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-6 md:px-12">
        <h2 className="text-2xl font-bold mb-8 text-center" style={{ fontFamily: "'Poppins', sans-serif" }}>How It Works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4 items-center mb-16">
          {steps.map((step, i) => (
            <Fragment key={step.num}>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center mx-auto mb-4 text-lg font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>{step.num}</div>
                <h3 className="font-semibold mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>{step.title}</h3>
                <p className="text-sm text-gray-500" style={{ fontFamily: "'Poppins', sans-serif" }}>{step.desc}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="hidden lg:flex items-center justify-center">
                  <ChevronRight size={28} className="text-[#D4AF37]" />
                </div>
              )}
            </Fragment>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            {submitted ? (
              <div className="text-center py-12" data-testid="sell-success">
                <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Quote Request Received</h3>
                <p className="text-gray-500 mb-6">We'll be in touch within 24 hours with an indicative price.</p>
                <Button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', address: '', message: '', product_name: '' }); setMetalType(''); }} className="btn-gold">Submit Another Request</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="sell-form">
                <h3 className="text-xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Request a Sell Quote</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Full Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="bg-white" data-testid="sell-name" /></div>
                  <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="bg-white" data-testid="sell-email" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Phone *</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required className="bg-white" data-testid="sell-phone" /></div>
                  <div>
                    <Label>Metal Type *</Label>
                    <Select value={metalType} onValueChange={setMetalType} required>
                      <SelectTrigger className="bg-white" data-testid="sell-metal-type"><SelectValue placeholder="Select metal" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gold-bars">Gold Bars</SelectItem>
                        <SelectItem value="gold-coins">Gold Coins</SelectItem>
                        <SelectItem value="silver-bars">Silver Bars</SelectItem>
                        <SelectItem value="silver-coins">Silver Coins</SelectItem>
                        <SelectItem value="platinum">Platinum</SelectItem>
                        <SelectItem value="jewellery-scrap">Jewellery / Scrap Metal</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>NZ Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-white" data-testid="sell-address" /></div>
                <div><Label>Describe what you'd like to sell *</Label><Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={4} required className="bg-white" placeholder="e.g., 2x 1oz gold bars, several silver coins..." data-testid="sell-message" /></div>
                <Button type="submit" disabled={submitting} className="btn-gold w-full text-sm" data-testid="sell-submit">
                  {submitting ? 'Submitting...' : 'Request Sell Quote'}
                </Button>
              </form>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-[#FAFAFA] p-8 rounded-sm">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>What We Buy</h3>
              <ul className="space-y-3 text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {whatWeBuy.map(item => (
                  <li key={item} className="flex items-start gap-2"><CheckCircle size={16} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-[#0A0A0A] p-8 rounded-sm text-white">
              <h3 className="text-lg font-bold mb-4 text-[#D4AF37]" style={{ fontFamily: "'Poppins', sans-serif" }}>Visit Us In Person</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2"><MapPin size={16} className="text-[#D4AF37] mt-0.5" /><span>Shop 9 Bishopdale Court, Bishopdale 8053, Christchurch</span></div>
                <div className="flex items-start gap-2"><Phone size={16} className="text-[#D4AF37] mt-0.5" /><div className="flex flex-col"><a href="tel:0211531763" className="hover:text-[#D4AF37]">021 153 1763</a><a href="tel:033596060" className="hover:text-[#D4AF37]">(03) 359 6060</a></div></div>
              </div>
              <p className="text-xs text-gray-400 mt-4">Walk-ins welcome. We provide immediate valuations in-store.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
